import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { MapMouseEvent } from 'mapbox-gl';
import type { UMMapHandle } from '../../urbanmap/_components';
import type { PlanGeoData } from '../proposition-c/types';
import { centerOf, intersectsArea } from './geoTools';
import type { MapOverlay } from './MapPane';

export function useMapInteraction(ref: RefObject<UMMapHandle>, geo: PlanGeoData, overlay: MapOverlay) {
  const [mapError, setMapError] = useState('');
  const latest = useRef(overlay);
  latest.current = overlay;
  useEffect(() => {
    let cleanup: (() => void) | undefined;
    const timer = setInterval(() => {
      const map = ref.current?.getMap();
      if (!map) return;
      clearInterval(timer);
      const failed = () => setMapError('Erreur cartographique : vérifiez le jeton Mapbox et la connexion. Le tableau reste utilisable.');
      map.on('error', failed);
      cleanup = () => { map.off('error', failed); };
    }, 25);
    return () => { clearInterval(timer); cleanup?.(); };
  }, [ref]);
  useEffect(() => {
    if (!overlay.focusedBuildingId && !overlay.focusedPointId) return;
    const point = overlay.technicalPoints?.find((item) => item.id === overlay.focusedPointId);
    const building = geo.buildings.features.find((feature) => feature.properties?.functionalId === overlay.focusedBuildingId);
    const center = point?.coordinates ?? (building?.geometry ? centerOf(building.geometry) : undefined);
    if (!center) return;
    const timer = setInterval(() => {
      if (!ref.current?.getMap()?.isStyleLoaded()) return;
      ref.current.flyTo(center, 17);
      clearInterval(timer);
    }, 100);
    return () => clearInterval(timer);
  }, [ref, geo, overlay.focusedBuildingId, overlay.focusedPointId, overlay.focusRequest]);

  useEffect(() => {
    if (!overlay.areaMode) return;
    let cleanup: (() => void) | undefined;
    const timer = setInterval(() => {
      const map = ref.current?.getMap();
      if (!map?.isStyleLoaded()) return;
      clearInterval(timer);
      const dragEnabled = map.dragPan.isEnabled();
      map.dragPan.disable();
      map.getCanvas().style.cursor = 'crosshair';
      let ring: [number, number][] = [];
      let drawing = false;
      const start = (event: MapMouseEvent) => {
        event.preventDefault();
        drawing = true;
        ring = [[event.lngLat.lng, event.lngLat.lat]];
      };
      const move = (event: MapMouseEvent) => {
        if (!drawing) return;
        ring.push([event.lngLat.lng, event.lngLat.lat]);
        const source = map.getSource('nav-lasso');
        if (source && 'setData' in source) source.setData({
          type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: ring },
        });
      };
      const end = () => {
        if (!drawing) return;
        drawing = false;
        if (ring.length < 3) {
          latest.current.onAreaError?.('Tracez une zone en maintenant le bouton de la souris enfoncé.');
          return;
        }
        const buildings = geo.buildings.features.filter((feature) => feature.geometry && intersectsArea(feature.geometry, ring))
          .map((feature) => String(feature.properties?.functionalId));
        const pipes = geo.pipes.features.flatMap((feature, index) =>
          feature.geometry && intersectsArea(feature.geometry, ring) ? [index] : []);
        const points = (latest.current.technicalPoints ?? []).filter((point) =>
          intersectsArea({ type: 'Point', coordinates: point.coordinates }, ring)).map((point) => point.id);
        latest.current.onAreaSelect?.({ buildings, pipes, points });
      };
      map.on('mousedown', start);
      map.on('mousemove', move);
      map.on('mouseup', end);
      cleanup = () => {
        map.off('mousedown', start); map.off('mousemove', move); map.off('mouseup', end);
        if (dragEnabled) map.dragPan.enable();
        map.getCanvas().style.cursor = '';
        const source = map.getSource('nav-lasso');
        if (source && 'setData' in source) source.setData({ type: 'FeatureCollection', features: [] });
      };
    }, 100);
    return () => { clearInterval(timer); cleanup?.(); };
  }, [ref, geo, overlay.areaMode]);
  return mapError;
}
